import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { CREATE_SANCTION } from "../season/mutations/Sanction";
import { GlobalStyles } from "../../constants/styles";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

/**
 * GameSanctionModal
 * - Receives a single game event (schedule row event) and creates a sanction tied to that game.
 *
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - season: { id, name } (optional, just for subtitle)
 * - gameEvent: the row `event` you have in GameElement (contains .game)
 * - onCreated: async () => void  (optional refresh hook)
 */
export default function GameSanctionModal({
                                              open,
                                              onClose,
                                              season,
                                              gameEvent,
                                              onCreated,
                                          }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [createSanction] = useMutation(CREATE_SANCTION);

    const game = gameEvent?.game;

    const [submitting, setSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        gameId: "",
        teamId: "",
        profileId: "",
        type: "",
        reason: "",
        notes: "",
        gamesSuspended: "",
    });

    const [teamsForGame, setTeamsForGame] = useState([]);
    const [profilesForTeam, setProfilesForTeam] = useState([]);

    const teamOptions = useMemo(() => {
        if (!game?.id) return [];

        return [
            {
                id: game?.homeTeam?.id,
                name:
                    game?.homeTeam?.name ||
                    game?.homeTeamAlias ||
                    t("gameSanctionModal.fallbacks.home"),
                players: game?.homeTeam?.players || [],
            },
            {
                id: game?.visitorTeam?.id,
                name:
                    game?.visitorTeam?.name ||
                    game?.visitorTeamAlias ||
                    t("gameSanctionModal.fallbacks.visitor"),
                players: game?.visitorTeam?.players || [],
            },
        ].filter((team) => team?.id);
    }, [
        game?.id,
        game?.homeTeam,
        game?.visitorTeam,
        game?.homeTeamAlias,
        game?.visitorTeamAlias,
        t,
    ]);

    useEffect(() => {
        if (!open) return;

        const gid = game?.id ? String(game.id) : "";
        setTeamsForGame(teamOptions);
        setProfilesForTeam([]);

        setFormData({
            gameId: gid,
            teamId: "",
            profileId: "",
            type: "",
            reason: "",
            notes: "",
            gamesSuspended: "",
        });
    }, [open, game?.id, teamOptions]);

    useEffect(() => {
        if (!open) return;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    const closeModal = useCallback(() => {
        if (submitting) return;
        onClose?.();
    }, [onClose, submitting]);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") closeModal();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [open, closeModal]);

    const ui = useMemo(() => {
        return {
            modalBackdrop: { backgroundColor: "rgba(0,0,0,0.55)" },
            modalDialog: { maxWidth: 720 },
            modalContent: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            modalHeader: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
            },
            modalTitleWrap: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            modalBadge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            modalTitle: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            modalSub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                color: colors.textOnPrimary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
                flex: "0 0 auto",
            },
            modalBody: {
                padding: 14,
                background: "#fff",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
            },
            fieldLabel: {
                fontWeight: 900,
                fontSize: 14,
                color: "rgba(20,21,26,0.72)",
                marginBottom: 6,
            },
            modalFooter: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            btnGhost: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimaryModal: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.10)",
                background: GlobalStyles.colors.canary,
                color: GlobalStyles.colors.woodsmoke,
                cursor: "pointer",
                lineHeight: 1,
            },
            formGrid: {
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
            },
            formRowFull: { gridColumn: "1 / -1" },
            infoLine: {
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: 10,
            },
            pill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                fontWeight: 900,
                fontSize: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                lineHeight: 1,
                whiteSpace: "nowrap",
                background: "rgba(0,0,0,0.03)",
                color: "rgba(20,21,26,0.78)",
            },
        };
    }, [colors.primary, colors.textOnPrimary]);

    const handleTeamChange = (e) => {
        const teamId = e.target.value;
        const team = teamsForGame.find((team) => String(team.id) === String(teamId));
        setFormData((prev) => ({ ...prev, teamId, profileId: "" }));
        setProfilesForTeam(team?.players || []);
    };

    const handleInput = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const canSubmit =
        !!formData.gameId &&
        !!formData.teamId &&
        !!formData.profileId &&
        !!formData.type &&
        !!formData.reason;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) {
            toast.error(t("gameSanctionModal.messages.requiredFields"));
            return;
        }

        setSubmitting(true);
        try {
            const input = {
                gameId: formData.gameId,
                teamId: formData.teamId,
                profileId: formData.profileId,
                type: String(formData.type || "").toLowerCase(),
                reason: formData.reason,
                notes: formData.notes,
                gamesSuspended:
                    formData.gamesSuspended === ""
                        ? null
                        : Number(formData.gamesSuspended),
            };

            await createSanction({ variables: { input } });

            toast.success(t("gameSanctionModal.messages.created"));
            closeModal();
            await onCreated?.();
        } catch (err) {
            toast.error(getLocalizedGraphQLError(err, t, "gameSanctionModal.messages.failed"));
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    const visitorName =
        game?.visitorTeam?.name ||
        game?.visitorTeamAlias ||
        t("gameSanctionModal.fallbacks.visitor");

    const homeName =
        game?.homeTeam?.name ||
        game?.homeTeamAlias ||
        t("gameSanctionModal.fallbacks.home");

    const gameLabel = `${visitorName} @ ${homeName}`;

    return (
        <div
            id="kbola-game-sanction-modal"
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={ui.modalBackdrop}
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) closeModal();
            }}
        >
            <div className="modal-dialog" role="document" style={ui.modalDialog}>
                <div
                    className="modal-content"
                    style={ui.modalContent}
                    onMouseDown={(e) => e.stopPropagation()}
                >
                    <div style={ui.modalHeader}>
                        <div style={ui.modalTitleWrap}>
                            <span style={ui.modalBadge}>
                                {t("gameSanctionModal.new")}
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <h5 style={ui.modalTitle}>
                                    {t("gameSanctionModal.title")}
                                </h5>
                                <p style={ui.modalSub}>
                                    {t("gameSanctionModal.subtitle", {
                                        season:
                                            season?.name ||
                                            t("gameSanctionModal.fallbacks.season"),
                                    })}
                                </p>
                            </div>
                        </div>

                        <button
                            type="button"
                            style={ui.closeBtn}
                            onClick={closeModal}
                            title={t("gameSanctionModal.buttons.close")}
                            aria-label={t("gameSanctionModal.buttons.close")}
                            disabled={submitting}
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body" style={ui.modalBody}>
                            <div style={ui.infoLine}>
                                <span style={ui.pill}>
                                    <i className="fas fa-futbol" />
                                    {gameLabel}
                                </span>

                                {game?.matchDay != null && (
                                    <span style={ui.pill}>
                                        <i className="fas fa-calendar" />
                                        {t("gameSanctionModal.labels.matchweek", {
                                            value: game.matchDay,
                                        })}
                                    </span>
                                )}
                            </div>

                            <div style={ui.formGrid} className="kbola-sanction-grid">
                                <div>
                                    <label style={ui.fieldLabel}>
                                        {t("gameSanctionModal.labels.team")}
                                    </label>
                                    <select
                                        name="teamId"
                                        className="form-control kbola-input"
                                        value={formData.teamId}
                                        onChange={handleTeamChange}
                                        required
                                        disabled={submitting}
                                    >
                                        <option value="">
                                            {t("gameSanctionModal.placeholders.selectTeam")}
                                        </option>
                                        {teamsForGame.map((team) => (
                                            <option key={team.id} value={team.id}>
                                                {team.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={ui.fieldLabel}>
                                        {t("gameSanctionModal.labels.player")}
                                    </label>
                                    <select
                                        name="profileId"
                                        className="form-control kbola-input"
                                        value={formData.profileId}
                                        onChange={handleInput}
                                        required
                                        disabled={submitting || !formData.teamId}
                                    >
                                        <option value="">
                                            {t("gameSanctionModal.placeholders.selectPlayer")}
                                        </option>
                                        {profilesForTeam.map((player) => (
                                            <option key={player.playerId} value={player.playerId}>
                                                {player.fullName}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={ui.fieldLabel}>
                                        {t("gameSanctionModal.labels.type")}
                                    </label>
                                    <select
                                        name="type"
                                        className="form-control kbola-input"
                                        value={(formData.type || "").toLowerCase()}
                                        onChange={handleInput}
                                        required
                                        disabled={submitting}
                                    >
                                        <option value="">
                                            {t("gameSanctionModal.placeholders.selectType")}
                                        </option>
                                        <option value="suspension">
                                            {t("gameSanctionModal.types.suspension")}
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label style={ui.fieldLabel}>
                                        {t("gameSanctionModal.labels.gamesSuspended")}
                                    </label>
                                    <input
                                        type="number"
                                        name="gamesSuspended"
                                        className="form-control kbola-input"
                                        value={formData.gamesSuspended}
                                        onChange={handleInput}
                                        disabled={submitting}
                                        placeholder={t("gameSanctionModal.placeholders.gamesSuspended")}
                                        min={0}
                                    />
                                </div>

                                <div style={ui.formRowFull}>
                                    <label style={ui.fieldLabel}>
                                        {t("gameSanctionModal.labels.reason")}
                                    </label>
                                    <input
                                        name="reason"
                                        className="form-control kbola-input"
                                        value={formData.reason}
                                        onChange={handleInput}
                                        required
                                        disabled={submitting}
                                        placeholder={t("gameSanctionModal.placeholders.reason")}
                                    />
                                </div>

                                <div style={ui.formRowFull}>
                                    <label style={ui.fieldLabel}>
                                        {t("gameSanctionModal.labels.notes")}
                                    </label>
                                    <textarea
                                        name="notes"
                                        className="form-control kbola-input"
                                        value={formData.notes}
                                        onChange={handleInput}
                                        disabled={submitting}
                                        rows={4}
                                        placeholder={t("gameSanctionModal.placeholders.notes")}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer" style={ui.modalFooter}>
                            <button
                                type="button"
                                style={{ ...ui.btnGhost, opacity: submitting ? 0.7 : 1 }}
                                onClick={closeModal}
                                disabled={submitting}
                            >
                                {t("gameSanctionModal.buttons.cancel")}
                            </button>

                            <button
                                type="submit"
                                style={{
                                    ...ui.btnPrimaryModal,
                                    opacity: canSubmit ? 1 : 0.7,
                                }}
                                disabled={submitting || !canSubmit}
                            >
                                {submitting ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("gameSanctionModal.buttons.saving")}
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-plus" />
                                        {t("gameSanctionModal.buttons.add")}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>

                    <style>{`
            #kbola-game-sanction-modal .kbola-input{
              border-radius: 12px !important;
              border: 1px solid rgba(0,0,0,0.10) !important;
              font-weight: 800 !important;
              height: auto !important;
            }
            #kbola-game-sanction-modal .kbola-input:focus{
              outline: none !important;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18) !important;
              border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65) !important;
            }
            #kbola-game-sanction-modal textarea.kbola-input{
              resize: vertical;
            }
            @media (max-width: 820px){
              #kbola-game-sanction-modal .kbola-sanction-grid{
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
                </div>
            </div>
        </div>
    );
}
